// app/javascript/pages/Admin/Profile/Show.tsx
import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { ApiTokenManagerCard } from '@/components/shared/ApiTokenManagerCard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ThemePicker } from '@/components/shared/ThemePicker'
import {
  User, ShieldCheck, Mail, Sparkles, Edit3, Save, X,
  Layers, BookOpen, Users, CheckCircle2, Trophy, Coins
} from 'lucide-react'

interface AdminProfileData {
  id: number
  name: string
  email: string
  role: string
  avatar_color: string
  avatar_base64?: string | null
  avatar_url?: string | null
  preferred_language: string
  api_token: string | null
  member_since: string
}

interface StatsData {
  total_users: number
  total_questions: number
  total_tests: number
  total_attempts: number
  credits_managed: number
}

interface Props {
  profile: AdminProfileData
  stats: StatsData
}

export default function AdminProfileShow({ profile, stats }: Props) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({
    name: profile.name,
    preferred_language: profile.preferred_language || 'en'
  })

  const initials = form.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    router.patch('/admin/profile', { profile: form }, {
      onSuccess: () => setEditing(false)
    })
  }

  const statCards = [
    { icon: Users, label: 'Total Users', value: stats.total_users, color: 'text-primary' },
    { icon: Layers, label: 'Questions', value: stats.total_questions, color: 'text-secondary' },
    { icon: BookOpen, label: 'Tests Created', value: stats.total_tests, color: 'text-tertiary' },
    { icon: Trophy, label: 'Attempts Evaluated', value: stats.total_attempts, color: 'text-primary' },
    { icon: Coins, label: 'Managed Credits', value: stats.credits_managed, color: 'text-secondary' },
  ]

  return (
    <AppShell>
      <div className="space-y-6 max-w-5xl mx-auto pb-12">
        {/* Page Header */}
        <div className="flex items-center justify-between gap-3 border-b border-border/40 pb-3.5">
          <h1 className="text-xl sm:text-2xl font-extrabold flex items-center gap-2 text-foreground tracking-tight">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
            <span>Admin Profile & Settings</span>
          </h1>
          {!editing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              className="rounded-xl font-bold text-xs h-8 sm:h-9 border-primary/40 text-primary hover:bg-primary/10 shadow-xs cursor-pointer"
            >
              <Edit3 className="h-3.5 w-3.5 mr-1" /> Edit Profile
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(false)}
                className="rounded-xl font-bold text-xs h-8 sm:h-9 border-border text-foreground hover:bg-muted/40 cursor-pointer"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                className="rounded-xl font-bold text-xs h-8 sm:h-9 bg-primary text-primary-fg shadow-xs cursor-pointer"
              >
                <Save className="h-3.5 w-3.5 mr-1" /> Save
              </Button>
            </div>
          )}
        </div>

        {/* Top Info Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Admin User Overview Card */}
          <Card className="lg:col-span-2 rounded-2xl border border-border bg-card shadow-glass-sm p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
              {/* Photo / Initials Avatar */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-primary text-primary-fg flex items-center justify-center text-xl sm:text-2xl font-extrabold shadow-glass-sm overflow-hidden border-2 border-primary/40 shrink-0">
                {form.avatar_base64 || profile.avatar_url ? (
                  <img src={form.avatar_base64 || profile.avatar_url || ''} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  initials
                )}
              </div>

              {/* Profile Details */}
              <div className="flex-1 w-full space-y-3">
                {editing ? (
                  <form onSubmit={handleSave} className="space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-bold">Admin Name</Label>
                      <Input
                        value={form.name}
                        onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                        className="h-9 rounded-xl text-xs bg-background border-input"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-bold">Preferred Language</Label>
                      <select
                        value={form.preferred_language}
                        onChange={e => setForm(f => ({ ...f, preferred_language: e.target.value }))}
                        className="w-full h-9 rounded-xl border border-input bg-background px-3 py-1.5 text-xs text-foreground font-medium focus:ring-2 focus:ring-primary"
                      >
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="mr">Marathi</option>
                      </select>
                    </div>
                  </form>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Name</p>
                      <p className="font-extrabold text-base sm:text-lg text-foreground">{profile.name}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Email</p>
                      <p className="font-semibold text-xs sm:text-sm text-foreground truncate">{profile.email}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Role</p>
                      <Badge variant="default" className="capitalize mt-0.5">
                        {profile.role}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-wider">Member Since</p>
                      <p className="text-xs font-semibold text-foreground mt-0.5">{profile.member_since}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Theme & Portal Appearance */}
          <Card className="rounded-2xl border border-border bg-card shadow-glass-sm p-4 sm:p-5 flex flex-col justify-between">
            <div className="space-y-1">
              <h3 className="text-sm sm:text-base font-extrabold text-foreground flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-secondary shrink-0" /> Admin Theme
              </h3>
              <p className="text-xs text-muted-foreground">
                Personalize your admin dashboard aesthetic and color scheme.
              </p>
            </div>
            <div className="pt-4">
              <ThemePicker />
            </div>
          </Card>
        </div>

        {/* System Overview Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {statCards.map(s => {
            const Icon = s.icon
            return (
              <Card key={s.label} className="p-3 rounded-2xl border border-border bg-card shadow-glass-xs flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-base sm:text-lg font-extrabold text-foreground">{s.value}</p>
                  <p className="text-[10px] sm:text-[11px] text-muted-foreground font-bold truncate">{s.label}</p>
                </div>
              </Card>
            )
          })}
        </div>

        {/* API Access & Developer Token Management Card */}
        <ApiTokenManagerCard
          apiToken={profile.api_token}
          regenerateUrl="/admin/profile/regenerate_api_token"
          title="Admin API Access & Developer Token"
          description="Authenticate automated agents, scripts, and REST endpoints with staff-level privileges."
        />
      </div>
    </AppShell>
  )
}
